import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Configuration for all pages
 * Each page gets optimized meta tags for search engines
 */
const seoConfig = {
  '/': {
    title: 'Rocket Van Express | Fast Mini Moves & Deliveries in Stellenbosch & Cape Town',
    description: 'Same-day deliveries and mini moves in the Western Cape. WhatsApp-first service with real-time tracking, proof of delivery, and transparent pricing. Get a quote in minutes!',
    keywords: 'mini moves stellenbosch, delivery cape town, courier western cape, small removals, furniture delivery, student moves',
    ogImage: 'https://rocketvan.co.za/og-image.png'
  },
  '/deliveries': {
    title: 'Same-Day Delivery Services | Rocket Van Express Stellenbosch & Cape Town',
    description: 'Fast, reliable same-day delivery services in Stellenbosch, Cape Town and surrounding areas. From documents to furniture - we deliver it all with care and real-time tracking.',
    keywords: 'same day delivery stellenbosch, courier cape town, local delivery, package delivery western cape'
  },
  '/mini-moves': {
    title: 'Mini Moves & Small Removals | Rocket Van Express Western Cape',
    description: 'Affordable mini moves and small removals in Stellenbosch, Cape Town, Somerset West, and Paarl. Professional helpers, blanket protection, and WhatsApp updates included.',
    keywords: 'mini moves cape town, small removals stellenbosch, student moves, furniture removal western cape'
  },
  '/business': {
    title: 'Business Delivery Accounts | Rocket Van Express Commercial Services',
    description: 'Priority dispatch, monthly invoicing, and dedicated support for businesses. Join Cape Town and Stellenbosch businesses using Rocket Van Express for reliable commercial deliveries.',
    keywords: 'business delivery cape town, commercial courier, b2b delivery stellenbosch, business logistics'
  },
  '/areas': {
    title: 'Service Areas | Rocket Van Express Delivery Coverage Map',
    description: 'Rocket Van Express delivers across the Western Cape including Stellenbosch, Cape Town CBD, Somerset West, Paarl, Franschhoek, and more. Check if we deliver to your area.',
    keywords: 'delivery areas cape town, service coverage stellenbosch, courier western cape areas'
  },
  '/track': {
    title: 'Track Your Delivery | Rocket Van Express Order Tracking',
    description: 'Track your Rocket Van Express delivery in real-time. Enter your booking reference for live status updates, or get tracking info straight to your WhatsApp.',
    keywords: 'track delivery, order tracking, courier tracking cape town'
  },
  '/contact': {
    title: 'Contact Us | Rocket Van Express Support & Inquiries',
    description: 'Get in touch with Rocket Van Express for quotes, support, or business inquiries. WhatsApp, call, or email - we respond within minutes during business hours.',
    keywords: 'contact rocket van express, delivery support, courier contact cape town'
  },
  '/pricing': {
    title: 'Pricing Guide & Estimator | Rocket Van Express Transparent Rates',
    description: 'Get an instant delivery estimate with our pricing calculator. Transparent, distance-based pricing for deliveries and mini moves in the Western Cape.',
    keywords: 'delivery prices cape town, mini move cost, courier rates stellenbosch'
  },
  '/referral': {
    title: 'Student Referral Program | Rocket Van Express Earn Free Deliveries',
    description: 'Refer friends to Rocket Van Express and earn free delivery credits. Perfect for students - share your code and get rewarded when friends book.',
    keywords: 'referral program, student discount, free delivery, refer a friend'
  },
  '/support': {
    title: 'Support Center | Rocket Van Express Help & Claims',
    description: 'Need help with your delivery? Track orders, reschedule, report issues, or submit claims. Our support team is here to help via WhatsApp.',
    keywords: 'delivery support, courier help, claims, reschedule delivery'
  },
  '/partners': {
    title: 'Partner With Rocket Van Express | Business & University Partnerships',
    description: 'Join Rocket Van Express partner network. Special rates for universities (UCT, Stellenbosch, UWC, CPUT) and businesses. Co-branded pages and priority service.',
    keywords: 'delivery partner, university moving partner, business partnership'
  },
  '/terms': {
    title: 'Terms & Conditions | Rocket Van Express Service Agreement',
    description: 'Rocket Van Express terms of service, liability, and service agreement for deliveries and mini moves in the Western Cape, South Africa.',
    keywords: 'terms of service, delivery agreement'
  },
  '/privacy': {
    title: 'Privacy Policy (POPIA) | Rocket Van Express Data Protection',
    description: 'Rocket Van Express privacy policy compliant with POPIA. Learn how we collect, use, and protect your personal information.',
    keywords: 'privacy policy, POPIA, data protection south africa'
  },
  '/cookies': {
    title: 'Cookie Policy | Rocket Van Express Website Cookies',
    description: 'Rocket Van Express cookie policy explaining how we use cookies to improve your experience. Manage your preferences anytime.',
    keywords: 'cookie policy, website cookies'
  },
  '/launch': {
    title: 'Launch Special Offer | Rocket Van Express Limited Time Deal',
    description: 'Special launch offer from Rocket Van Express! Get exclusive rates on your first delivery or mini move in Stellenbosch and Cape Town.',
    keywords: 'delivery discount, moving special offer, launch promotion'
  }
};

// Default SEO for pages not in config
const defaultSeo = {
  title: 'Rocket Van Express | Mini Moves & Deliveries',
  description: 'Fast and reliable mini moves and deliveries in Stellenbosch and Cape Town. WhatsApp-first service with real-time tracking.',
  keywords: 'mini moves, delivery, courier, cape town, stellenbosch'
};

/**
 * SEOHead component - updates document head with page-specific meta tags
 */
const SEOHead = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    
    // Get SEO config for current path or use default
    let seo = seoConfig[path] || defaultSeo;
    
    // Handle dynamic routes
    if (path.startsWith('/areas/')) {
      const areaSlug = path.replace('/areas/', '');
      const areaName = areaSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      seo = {
        title: `Delivery & Mini Moves in ${areaName} | Rocket Van Express`,
        description: `Fast, reliable deliveries and mini moves in ${areaName}. Same-day service, real-time tracking, and WhatsApp updates. Get a quote now!`,
        keywords: `delivery ${areaName.toLowerCase()}, mini moves ${areaName.toLowerCase()}, courier ${areaName.toLowerCase()}`
      };
    }
    
    if (path.startsWith('/partners/')) {
      const partnerSlug = path.replace('/partners/', '');
      const partnerName = partnerSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      seo = {
        title: `${partnerName} Moving Partner | Rocket Van Express`,
        description: `Special delivery and moving rates for ${partnerName}. Student discounts, priority service, and WhatsApp tracking.`,
        keywords: `${partnerName.toLowerCase()} delivery, ${partnerName.toLowerCase()} moving, student moves`
      };
    }

    // Update document title
    document.title = seo.title;

    // Update meta description
    let descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) {
      descMeta.setAttribute('content', seo.description);
    }

    // Update meta keywords
    let keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (!keywordsMeta) {
      keywordsMeta = document.createElement('meta');
      keywordsMeta.name = 'keywords';
      document.head.appendChild(keywordsMeta);
    }
    keywordsMeta.setAttribute('content', seo.keywords || defaultSeo.keywords);

    // Update Open Graph tags
    const ogTags = {
      'og:title': seo.title,
      'og:description': seo.description,
      'og:url': `https://rocketvan.co.za${path}`,
      'og:type': 'website',
      'og:site_name': 'Rocket Van Express',
      'og:locale': 'en_ZA'
    };

    Object.entries(ogTags).forEach(([property, content]) => {
      let ogMeta = document.querySelector(`meta[property="${property}"]`);
      if (!ogMeta) {
        ogMeta = document.createElement('meta');
        ogMeta.setAttribute('property', property);
        document.head.appendChild(ogMeta);
      }
      ogMeta.setAttribute('content', content);
    });

    // Update Twitter Card tags
    const twitterTags = {
      'twitter:card': 'summary_large_image',
      'twitter:title': seo.title,
      'twitter:description': seo.description
    };

    Object.entries(twitterTags).forEach(([name, content]) => {
      let twitterMeta = document.querySelector(`meta[name="${name}"]`);
      if (!twitterMeta) {
        twitterMeta = document.createElement('meta');
        twitterMeta.name = name;
        document.head.appendChild(twitterMeta);
      }
      twitterMeta.setAttribute('content', content);
    });

    // Add canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = `https://rocketvan.co.za${path}`;

  }, [location.pathname]);

  return null;
};

export { SEOHead, seoConfig };
export default SEOHead;
